import React, { useState } from 'react';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type {
  NurturingStage,
  NurturingStep,
  StepType,
  CadenceUnit,
  StaticStepHoverData,
} from 'app/pages/NurturingJourneySettingsComponent/types';
import {
  STEP_TYPES,
  CADENCE_UNITS,
} from 'app/pages/NurturingJourneySettingsComponent/types';
import {
  STEP_TYPE_LABELS,
  CADENCE_UNIT_LABELS,
  DSL_NAME_OPTIONS,
  TEMPLATE_TYPE_OPTIONS,
} from 'app/pages/NurturingJourneySettingsComponent/data/constants';
import { NurturingStepCard } from './NurturingStepCard';

const sortableIdPrefix = 'nurturing-step-';

interface AddStepFormState {
  templateType: 'dynamic' | 'static';
  stepType: StepType;
  cadenceValue: number;
  cadenceUnit: CadenceUnit;
  templateTypes: string[];
  dslName: string;
  alternateOptions: string[];
}

const defaultAddStepForm: AddStepFormState = {
  templateType: 'dynamic',
  stepType: 'fixed',
  cadenceValue: 6,
  cadenceUnit: 'days',
  templateTypes: [],
  dslName: '',
  alternateOptions: [],
};

export interface NurturingStagePanelProps {
  stageKey: string;
  stageLabel: string;
  stage: NurturingStage;
  segmentIndex: number;
  recordType: 'unsold' | 'sold';
  onReorder: (oldIndex: number, newIndex: number) => void;
  onUpdateStep: (stepIndex: number, patch: Partial<NurturingStep>) => void;
  onUpdateMaxRepeat: (value: number | null) => void;
  onAddStep: (step: NurturingStep) => void;
  onDeleteStep: (stepIndex: number) => void;
  getStaticStepHoverData?: (
    steps: NurturingStep[],
    staticStepIndex: number,
  ) => StaticStepHoverData;
  /** Fired when a drag would move Step 0 or place another step in its slot */
  onStepZeroReorderBlocked?: () => void;
  onImportStepsFromAllSegment?: () => void;
  disableImportStepsFromAllSegment?: boolean;
  disabled?: boolean;
}

function sortSteps(steps: NurturingStep[]): NurturingStep[] {
  return [...steps].sort((a, b) => a.step_order - b.step_order);
}

function buildNewStep(form: AddStepFormState, stepOrder: number): NurturingStep {
  const base = {
    step_order: stepOrder,
    generated_template_type: form.templateType,
    cadence_value: form.cadenceValue,
    cadence_unit: form.cadenceUnit,
  };
  if (form.stepType === 'fixed') {
    return { ...base, step_type: 'fixed', template_types: form.templateTypes };
  }
  if (form.stepType === 'value_associated_with_previous') {
    return { ...base, step_type: 'value_associated_with_previous', dsl_name: form.dslName };
  }
  return { ...base, step_type: 'alternate', alternate_options: form.alternateOptions, alternate_based_on_previous: false };
}

export function NurturingStagePanel({
  stageKey,
  stageLabel,
  stage,
  segmentIndex,
  recordType,
  onReorder,
  onUpdateStep,
  onUpdateMaxRepeat,
  onAddStep,
  onDeleteStep,
  getStaticStepHoverData,
  onStepZeroReorderBlocked,
  onImportStepsFromAllSegment,
  disableImportStepsFromAllSegment,
  disabled,
}: NurturingStagePanelProps) {
  const allowAddDelete = recordType === 'unsold' && stageKey === 'retargeting';
  const sortedSteps = sortSteps(stage.steps || []);
  const sortableIds = sortedSteps.map(
    (_, i) => `${sortableIdPrefix}-${segmentIndex}-${recordType}-${stageKey}-${i}`,
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableIds.indexOf(String(active.id));
    const newIndex = sortableIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const noopAnchorIndex = sortedSteps.findIndex(s => s.step_order === 0);
    if (
      noopAnchorIndex !== -1 &&
      (oldIndex === noopAnchorIndex || newIndex === noopAnchorIndex)
    ) {
      onStepZeroReorderBlocked?.();
      return;
    }

    onReorder(oldIndex, newIndex);
  };

  const handleMaxRepeatChange = (e: any) => {
    const val = e.target.value;
    if (val === '') {
      onUpdateMaxRepeat(null);
    } else {
      onUpdateMaxRepeat(Number(val));
    }
  };

  // --- Add Step Dialog state ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddStepFormState>(defaultAddStepForm);

  // --- Delete confirmation state ---
  const [deleteConfirmStepIndex, setDeleteConfirmStepIndex] = useState<number | null>(null);

  const handleOpenDialog = () => {
    setAddForm(defaultAddStepForm);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleConfirmAdd = () => {
    const maxOrder = sortedSteps.reduce((m, s) => Math.max(m, s.step_order), 0);
    const newStep = buildNewStep(addForm, maxOrder + 1);
    onAddStep(newStep);
    setDialogOpen(false);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
          py: 1,
          px: 1.5,
          borderRadius: 1,
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
          Max repeat
        </Typography>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={stage.max_repeat == null ? '' : String(stage.max_repeat)}
            onChange={handleMaxRepeatChange}
            disabled={disabled}
            displayEmpty
            renderValue={val => (val === '' ? <em>—</em> : val)}
            MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
          >
            <MenuItem value=""><em>—</em></MenuItem>
            {Array.from({ length: 100 }, (_, i) => i + 1).map(n => (
              <MenuItem key={n} value={String(n)}>{n}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {allowAddDelete && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={onImportStepsFromAllSegment}
              disabled={
                disabled ||
                disableImportStepsFromAllSegment ||
                !onImportStepsFromAllSegment
              }
            >
              Import Default Steps
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              disabled={disabled}
            >
              Add Step
            </Button>
          </Box>
        )}
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {sortedSteps.map((step, i) => (
            <NurturingStepCard
              key={`${stageKey}-${step.step_order}-${i}`}
              step={step}
              stepIndex={i}
              sortableId={sortableIds[i]}
              onUpdate={patch => onUpdateStep(i, patch)}
              onDelete={
                allowAddDelete && step.step_order !== 0
                  ? () => setDeleteConfirmStepIndex(i)
                  : undefined
              }
              staticStepHoverData={
                getStaticStepHoverData
                  ? getStaticStepHoverData(sortedSteps, i)
                  : { previousDynamicType: null, allowedStaticTypes: [], mappedByDynamicType: {} }
              }
              disabled={disabled}
            />
          ))}
        </SortableContext>
      </DndContext>

      {sortedSteps.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No steps in this stage.
        </Typography>
      )}

      {/* Add Step Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Step</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          {/* Template Type toggle */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Template Type
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={addForm.templateType}
              onChange={(_, val) => {
                if (!val) return;
                const defaultStepType: StepType =
                  val === 'static' ? 'value_associated_with_previous' : 'fixed';
                setAddForm(prev => ({
                  ...prev,
                  templateType: val,
                  stepType: defaultStepType,
                  templateTypes: [],
                  dslName: '',
                  alternateOptions: [],
                }));
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="dynamic" sx={{ textTransform: 'none', fontWeight: 600 }}>
                Dynamic Template
              </ToggleButton>
              <ToggleButton value="static" sx={{ textTransform: 'none', fontWeight: 600 }}>
                Static Template
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Step Type — filtered by template type */}
          <FormControl size="small" fullWidth>
            <InputLabel>Step Type</InputLabel>
            <Select
              label="Step Type"
              value={addForm.stepType}
              onChange={e =>
                setAddForm(prev => ({
                  ...prev,
                  stepType: e.target.value as StepType,
                  templateTypes: [],
                  dslName: '',
                  alternateOptions: [],
                }))
              }
            >
              {(addForm.templateType === 'static'
                ? (['value_associated_with_previous'] as StepType[])
                : (['fixed', 'alternate'] as StepType[])
              ).map(t => (
                <MenuItem key={t} value={t}>
                  {STEP_TYPE_LABELS[t] ?? t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Type-specific fields */}
          {addForm.stepType === 'fixed' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Template Types</InputLabel>
              <Select
                multiple
                label="Template Types"
                value={addForm.templateTypes}
                onChange={e =>
                  setAddForm(prev => ({ ...prev, templateTypes: e.target.value as string[] }))
                }
                input={<OutlinedInput label="Template Types" />}
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map(v => (
                      <Chip key={v} label={v} size="small" />
                    ))}
                  </Box>
                )}
              >
                {TEMPLATE_TYPE_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    <Checkbox checked={addForm.templateTypes.includes(opt)} size="small" />
                    <ListItemText primary={opt} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {addForm.stepType === 'value_associated_with_previous' && (
            <FormControl size="small" fullWidth>
              <InputLabel>DSL Name</InputLabel>
              <Select
                label="DSL Name"
                value={addForm.dslName}
                onChange={e => setAddForm(prev => ({ ...prev, dslName: e.target.value }))}
              >
                {DSL_NAME_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {addForm.stepType === 'alternate' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Alternate Options</InputLabel>
              <Select
                multiple
                label="Alternate Options"
                value={addForm.alternateOptions}
                onChange={e =>
                  setAddForm(prev => ({ ...prev, alternateOptions: e.target.value as string[] }))
                }
                input={<OutlinedInput label="Alternate Options" />}
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map(v => (
                      <Chip key={v} label={v} size="small" />
                    ))}
                  </Box>
                )}
              >
                {TEMPLATE_TYPE_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    <Checkbox checked={addForm.alternateOptions.includes(opt)} size="small" />
                    <ListItemText primary={opt} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Cadence row */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              label="Cadence"
              size="small"
              type="number"
              value={addForm.cadenceValue}
              onChange={e => {
                const n = parseInt(e.target.value, 10);
                if (!Number.isNaN(n) && n >= 0)
                  setAddForm(prev => ({ ...prev, cadenceValue: n }));
              }}
              inputProps={{ min: 0, step: 1, onWheel: e => (e.target as HTMLInputElement).blur() }}
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Unit</InputLabel>
              <Select
                label="Unit"
                value={addForm.cadenceUnit}
                onChange={e =>
                  setAddForm(prev => ({ ...prev, cadenceUnit: e.target.value as CadenceUnit }))
                }
              >
                {CADENCE_UNITS.map(u => (
                  <MenuItem key={u} value={u}>
                    {CADENCE_UNIT_LABELS[u] ?? u}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined" size="small">
            Cancel
          </Button>
          <Button onClick={handleConfirmAdd} variant="contained" size="small">
            Add Step
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialogMAIQ
        open={deleteConfirmStepIndex !== null}
        onClose={() => setDeleteConfirmStepIndex(null)}
        onConfirm={() => {
          if (deleteConfirmStepIndex !== null) {
            onDeleteStep(deleteConfirmStepIndex);
            setDeleteConfirmStepIndex(null);
          }
        }}
        title="Delete Step?"
        message="Are you sure you want to delete this step? This action cannot be undone."
        confirmLabel="Delete"
      />
    </Box>
  );
}
